import styled from '@emotion/styled';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { isNumericText, isRejectedNumericKey } from 'shared/utils';
import { theme } from 'styles/theme/theme';

interface IProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'onSubmit' | 'onBlur' | 'onFocus'> {
  onChange?: (value: string) => void;
  onSubmit?: (value: string) => void;
  onBlur?: (value: string) => void;
  onFocus?: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  value: string;
  icon?: React.ReactNode;
  unit?: string;
  /** Числовое поле: буквы и прочие нечисловые символы в него не вводятся. */
  numeric?: boolean;
}

function Input(props: IProps) {
  const { onChange, onSubmit, onBlur, onFocus, value, icon, unit, onKeyDown, numeric, ...rest } = props;
  const [viewState, setViewState] = useState(value);
  const [isFocus, setIsFocus] = useState(false);

  useEffect(() => {
    setViewState(value);
  }, [value]);

  useEffect(() => {
    if (isFocus) {
      // Снимаем единицу измерения («10px» → «10») на время редактирования.
      // Раньше здесь оставались только цифры — у отрицательной координаты
      // при клике в поле пропадал минус, а у дробной — точка.
      setViewState(numeric ? value.replace(/[^0-9.,-]/g, '') : value.replace(/\D/g, ''));
    } else {
      setViewState(value + (unit ?? ''));
    }

    // eslint-disable-next-line
  }, [isFocus]);

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setViewState(value);
    if (onChange) onChange(value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Буква в числовом поле уходила в объект холста как NaN и ломала элемент —
    // отсекаем её до того, как она попадёт в значение.
    if (numeric && isRejectedNumericKey(event)) {
      event.preventDefault();
      return;
    }

    // Если передан пользовательский обработчик, используем его
    if (onKeyDown) {
      onKeyDown(event);
      return;
    }

    // Базовая логика по умолчанию
    if (event.key === 'Enter') {
      if (!event.target) return;
      if (onSubmit) {
        onSubmit((event.target as HTMLInputElement).value);
      }
    }
  };

  const onBlurInput = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocus(false);
    if (!event.target) return;
    if (onBlur) {
      onBlur((event.target as HTMLInputElement).value);
    }
  };

  const onFocusInput = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocus(true);
    if (!event.target) return;
    if (onFocus) {
      onFocus((event.target as HTMLInputElement).value);
    }
  };

  const onPasteInput = (event: React.ClipboardEvent<HTMLInputElement>) => {
    if (numeric && !isNumericText(event.clipboardData?.getData('text') ?? '')) {
      event.preventDefault();
    }
  };

  return (
    <MainBlock {...rest}>
      {icon}
      <InputBlock
        onFocus={onFocusInput}
        onBlur={onBlurInput}
        onKeyDown={handleKeyDown}
        onPaste={onPasteInput}
        value={viewState}
        onChange={onChangeInput}
      />
    </MainBlock>
  );
}

export default Input;

export const IconWrapper = styled.span`
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-weight: 400;
  text-align: center;
  color: ${theme.colors.Gray96};
  min-height: 16px;
  min-width: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MainBlock = styled.div`
  border-radius: 8px;
  background: ${theme.colors.SurfaceCultured10};

  height: 32px;

  padding: 8px 8px 8px 4px;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const InputBlock = styled.input`
  width: 100%;
  height: 100%;
  background: transparent;
  border: none;
  outline: none;
  margin-left: 4px;

  font-family: Inter, sans-serif;
  font-size: 11px;
  font-weight: 400;
  text-align: left;
`;
